import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import { signupApi, sendOtpApi, verifyOtpApi, loginApi } from "@/features/auth/model/authApi";
import { tokenService } from "@/services/auth/token.service";
import { toastService } from "@/shared/toast";
import { useAuth } from "@/app/providers/useAuth";
import { useLoginViewModel } from "@/features/auth/viewmodel/useLoginViewModel";
import { neighborhoodUnlockHouse } from "@/assets/images";
import styles from "./ProtestRegistrationModal.module.css";
import type { SignupApiPayload } from "@/features/auth/model/authTypes";

const COUNTRIES = [
  {
    code: "+1",
    name: "USA",
    flag: (
      <svg
        width="20"
        height="15"
        viewBox="0 0 20 15"
        fill="none"
        style={{ borderRadius: "2px", flexShrink: 0 }}
      >
        <rect width="20" height="15" fill="#B22234" />
        <path
          d="M0 2.3h20M0 4.6h20M0 6.9h20M0 9.2h20M0 11.5h20M0 13.8h20"
          stroke="#FFFFFF"
          strokeWidth="1.15"
        />
        <rect width="8" height="8.1" fill="#3C3B6E" />
        <circle cx="2" cy="1.6" r="0.6" fill="#FFFFFF" />
        <circle cx="4" cy="1.6" r="0.6" fill="#FFFFFF" />
        <circle cx="6" cy="1.6" r="0.6" fill="#FFFFFF" />
        <circle cx="3" cy="2.8" r="0.6" fill="#FFFFFF" />
        <circle cx="5" cy="2.8" r="0.6" fill="#FFFFFF" />
        <circle cx="2" cy="4.0" r="0.6" fill="#FFFFFF" />
        <circle cx="4" cy="4.0" r="0.6" fill="#FFFFFF" />
        <circle cx="6" cy="4.0" r="0.6" fill="#FFFFFF" />
        <circle cx="3" cy="5.2" r="0.6" fill="#FFFFFF" />
        <circle cx="5" cy="5.2" r="0.6" fill="#FFFFFF" />
        <circle cx="2" cy="6.4" r="0.6" fill="#FFFFFF" />
        <circle cx="4" cy="6.4" r="0.6" fill="#FFFFFF" />
        <circle cx="6" cy="6.4" r="0.6" fill="#FFFFFF" />
      </svg>
    ),
  },
  {
    code: "+91",
    name: "India",
    flag: (
      <svg
        width="20"
        height="15"
        viewBox="0 0 20 15"
        fill="none"
        style={{ borderRadius: "2px", flexShrink: 0 }}
      >
        <rect width="20" height="5" fill="#FF9933" />
        <rect y="5" width="20" height="5" fill="#FFFFFF" />
        <rect y="10" width="20" height="5" fill="#138808" />
        <circle cx="10" cy="7.5" r="1.8" stroke="#000080" strokeWidth="0.5" fill="none" />
        <circle cx="10" cy="7.5" r="0.4" fill="#000080" />
        <path
          d="M10 5.7v3.6M8.2 7.5h3.6M8.7 6.2l2.6 2.6M8.7 8.8l2.6-2.6"
          stroke="#000080"
          strokeWidth="0.3"
        />
      </svg>
    ),
  },
] as const;

type CountryCode = "+1" | "+91";

interface ProtestRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  onSuccess?: () => void;
}

export const ProtestRegistrationModal: React.FC<ProtestRegistrationModalProps> = ({
  isOpen,
  onClose,
  initialEmail = "",
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { handleGoogleLogin, handleAppleLogin } = useLoginViewModel();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isLoading, setIsLoading] = useState(false);

  // Step 1 Details
  const [email, setEmail] = useState(initialEmail);
  const [confirmEmail, setConfirmEmail] = useState(initialEmail);
  const [firstName, setFirstName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  useEffect(() => {
    if (isOpen && initialEmail) {
      setEmail(initialEmail);
      setConfirmEmail(initialEmail);
    }
  }, [isOpen, initialEmail]);

  // Step 2 Mobile
  const [countryCode, setCountryCode] = useState<CountryCode>("+1");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileError, setMobileError] = useState("");

  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    if (isCountryDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCountryDropdownOpen]);

  const handleMobileNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileNumber(onlyDigits);
    if (mobileError) setMobileError("");
  };

  // Step 3 OTP
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");

  const otpArray = otp.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleOtpChange = (value: string, index: number) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtp(newOtp.join(""));

    if (value && index < 5) {
      const next = document.getElementById(`modal-otp-${index + 1}`);
      next?.focus();
    }

    if (!value && index > 0) {
      const prev = document.getElementById(`modal-otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      const prev = document.getElementById(`modal-otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleNextStep1 = () => {
    if (!email || !firstName || !password || !confirmPassword) {
      setDetailsError("Please fill in all fields.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setDetailsError("Please enter a valid email address.");
      return;
    }

    if (confirmEmail && !emailRegex.test(confirmEmail)) {
      setDetailsError("Please enter a valid confirmation email address.");
      return;
    }

    if (confirmEmail && confirmEmail.trim().toLowerCase() !== email.trim().toLowerCase()) {
      setDetailsError("Email address and Confirm email address must match.");
      return;
    }

    if (password.length < 6) {
      setDetailsError("Password must be at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setDetailsError("Password and Confirm password must match.");
      return;
    }

    setDetailsError("");
    setStep(2);
  };

  const handleSendOtp = async () => {
    if (!mobileNumber) {
      setMobileError("Please enter your mobile number.");
      return;
    }

    const cleanedDigits = mobileNumber.replace(/\D/g, "");
    if (cleanedDigits.length !== 10) {
      setMobileError("Please enter a valid 10-digit mobile number.");
      return;
    }

    const formattedPhone = `${countryCode}${cleanedDigits}`;

    setMobileError("");
    setIsLoading(true);

    try {
      await sendOtpApi({ phone: formattedPhone });
      setStep(3);
    } catch (error) {
      console.error(error);
      const err = error as { response?: { data?: { message?: string } } };
      setMobileError(err?.response?.data?.message || "Failed to send code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError("Please enter a valid code.");
      return;
    }

    setOtpError("");
    setIsLoading(true);

    const formattedPhone = `${countryCode}${mobileNumber.replace(/\D/g, "")}`;

    try {
      const response = await verifyOtpApi({ phone: formattedPhone, otp });

      const res = response as {
        data?: { token?: string; data?: { token?: string } };
        token?: string;
      };
      const token = res?.data?.token || res?.token || res?.data?.data?.token || "";
      if (token) {
        tokenService.setToken(token);
      }

      toastService.show("Verification successful!", "success");

      // Now register the user
      try {
        const nameParts = firstName.trim().split(/\s+/);
        const fName = nameParts[0] || firstName.trim();
        const lName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : fName || "User";

        const signupPayload: SignupApiPayload = {
          firstName: fName,
          lastName: lName,
          email: email.trim(),
          password,
          phone: formattedPhone,
          role: 2,
          referal_code: "",
        };
        await signupApi(signupPayload);

        // Call login API
        const user = await loginApi({ email: email.trim(), password, role: 2 });
        const tempToken = sessionStorage.getItem("temp_token");
        if (tempToken) {
          tokenService.setToken(tempToken);
        }
        login(user);
        toastService.show("Logged in successfully!", "success");
      } catch (authError) {
        console.error("Signup/Login API failed:", authError);
        const err = authError as { response?: { data?: { message?: string } } };
        setOtpError(err?.response?.data?.message || "Registration failed. Please try again.");
        setIsLoading(false);
        return;
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(routes.selectPropertyType);
      }
    } catch (error) {
      console.error(error);
      const err = error as { response?: { data?: { message?: string } } };
      setOtpError(err?.response?.data?.message || "Invalid code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setOtpError("");
    const formattedPhone = `${countryCode}${mobileNumber.replace(/\D/g, "")}`;
    try {
      await sendOtpApi({ phone: formattedPhone });
      toastService.show("OTP resent successfully", "success");
    } catch (error) {
      console.error(error);
      const err = error as { response?: { data?: { message?: string } } };
      setOtpError(err?.response?.data?.message || "Failed to resend code. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    onClose();
    navigate(routes.login);
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      // onClick={(e) => {
      //   if (e.target === e.currentTarget && !isLoading) {
      //     onClose();
      //   }
      // }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Right Side Visual Graphic */}
        <div
          className={styles.modalBackdropArt}
          style={{ backgroundImage: `url(${neighborhoodUnlockHouse})` }}
          aria-hidden="true"
        />

        {/* Close Button */}
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close modal">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          <div className={styles.contentColumn}>
            {/* Progress Bars */}
            <div className={styles.progressContainer}>
              <div
                className={`${styles.progressBar} ${step >= 1 ? styles.progressBarActive : ""}`}
              />
              <div
                className={`${styles.progressBar} ${step >= 2 ? styles.progressBarActive : ""}`}
              />
              <div
                className={`${styles.progressBar} ${step >= 3 ? styles.progressBarActive : ""}`}
              />
            </div>

            <div className={styles.stepTag}>STEP {step} OF 3</div>

            {/* Step 1: Account Details */}
            {step === 1 && (
              <>
                <h2 className={styles.title}>
                  Unlock your <span className={styles.titleGreen}>neighborhood</span>
                </h2>
                <p className={styles.subtitle}>
                  Add your name to see crime, offender and community data for your property.
                </p>

                <div className={styles.form}>
                  {/* Email Field */}
                  <div
                    className={`${styles.inputCard} ${
                      initialEmail ? styles.inputCardDisabled : ""
                    }`}
                  >
                    <div className={styles.inputIconBadge}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </div>
                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>Email address</label>
                      <input
                        type="email"
                        className={styles.fieldInput}
                        placeholder="Email address"
                        value={email}
                        disabled={Boolean(initialEmail)}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* First Name Field */}
                  <div className={styles.inputCard}>
                    <div className={styles.inputIconBadge}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>First name</label>
                      <input
                        type="text"
                        className={styles.fieldInput}
                        placeholder="First name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Confirm Email Address Field */}
                  <div className={styles.inputCard}>
                    <div className={styles.inputIconBadge}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </div>
                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>Confirm email address</label>
                      <input
                        type="email"
                        className={styles.fieldInput}
                        placeholder="Confirm email address"
                        value={confirmEmail}
                        onChange={(e) => setConfirmEmail(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className={styles.inputCard}>
                    <div className={styles.inputIconBadge}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>Password</label>
                      <input
                        type={showPassword ? "text" : "password"}
                        className={styles.fieldInput}
                        placeholder="••••••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className={styles.passwordToggleBtn}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Confirm Password Field */}
                  <div className={styles.inputCard}>
                    <div className={styles.inputIconBadge}>
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>Confirm password</label>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={styles.fieldInput}
                        placeholder="••••••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                      />
                    </div>
                    <button
                      type="button"
                      className={styles.passwordToggleBtn}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {detailsError && <span className={styles.errorText}>{detailsError}</span>}

                  {/* Continue Button */}
                  <button
                    className={styles.btnPrimary}
                    onClick={handleNextStep1}
                    disabled={isLoading}
                  >
                    <span>Continue</span>
                    <span className={styles.btnArrow}>→</span>
                  </button>

                  {/* Separator */}
                  <div className={styles.separator}>
                    <div className={styles.separatorLine} />
                    <span className={styles.separatorText}>Or sign up with</span>
                    <div className={styles.separatorLine} />
                  </div>

                  {/* Social Buttons */}
                  <div className={styles.socialButtons}>
                    <button
                      type="button"
                      className={styles.socialButton}
                      onClick={handleGoogleLogin}
                    >
                      <div className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                          />
                        </svg>
                      </div>
                      <span>Google</span>
                    </button>

                    <button
                      type="button"
                      className={styles.socialButton}
                      onClick={handleAppleLogin}
                    >
                      <div className={styles.socialIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-1.99.6-2.64 1.35-.56.65-1.06 1.71-.93 2.74 1.01.08 2.03-.5 2.65-1.24z" />
                        </svg>
                      </div>
                      <span>Apple</span>
                    </button>
                  </div>

                  {/* Terms */}
                  <p className={styles.termsText}>
                    By continuing you agree to our{" "}
                    <a href="#" className={styles.termsLink}>
                      Terms &amp; Privacy Policy
                    </a>
                  </p>
                </div>
              </>
            )}

            {/* Step 2: Phone Verification */}
            {step === 2 && (
              <>
                <h2 className={styles.title}>
                  Verify your <span className={styles.titleGreen}>number</span>
                </h2>
                <p className={styles.subtitle}>
                  We&apos;ll text you a code so we know how to reach you about your filing.
                </p>

                <div className={styles.form}>
                  <div className={styles.inputCard}>
                    <div className={styles.countryPickerContainer} ref={countryDropdownRef}>
                      <button
                        type="button"
                        className={styles.countryPickerBtn}
                        onClick={() => setIsCountryDropdownOpen((prev) => !prev)}
                        aria-label="Select country code"
                        aria-expanded={isCountryDropdownOpen}
                      >
                        <span className={styles.flagIcon}>{selectedCountry.flag}</span>
                        <span className={styles.countryCodeText}>{selectedCountry.code}</span>
                        <svg
                          className={`${styles.dropdownChevron} ${
                            isCountryDropdownOpen ? styles.dropdownChevronOpen : ""
                          }`}
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {isCountryDropdownOpen && (
                        <div className={styles.countryDropdownMenu}>
                          {COUNTRIES.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              className={`${styles.countryOption} ${
                                countryCode === c.code ? styles.countryOptionSelected : ""
                              }`}
                              onClick={() => {
                                setCountryCode(c.code as CountryCode);
                                setIsCountryDropdownOpen(false);
                              }}
                            >
                              <span className={styles.flagIcon}>{c.flag}</span>
                              <span className={styles.countryOptionLabel}>{c.name}</span>
                              <span className={styles.countryOptionCode}>{c.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.inputDivider} />

                    <div className={styles.inputContent}>
                      <label className={styles.floatingLabel}>Mobile number</label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        className={styles.fieldInput}
                        placeholder={countryCode === "+1" ? "5550000000" : "9876543210"}
                        value={mobileNumber}
                        onChange={handleMobileNumberChange}
                      />
                    </div>
                  </div>

                  {mobileError && <span className={styles.errorText}>{mobileError}</span>}

                  <button
                    className={styles.btnPrimary}
                    onClick={handleSendOtp}
                    disabled={isLoading}
                  >
                    <span>{isLoading ? "Sending..." : "Send code"}</span>
                    {!isLoading && <span className={styles.btnArrow}>→</span>}
                  </button>

                  {/* <button className={styles.btnSecondary} onClick={onClose}>
                    I&apos;ll finish this later
                  </button> */}
                </div>
              </>
            )}

            {/* Step 3: Enter OTP */}
            {step === 3 && (
              <>
                <h2 className={styles.title}>
                  Enter <span className={styles.titleGreen}>OTP</span>
                </h2>
                <p className={styles.subtitle}>
                  Enter the 6-digit code sent to your mobile number {countryCode} {mobileNumber}.
                </p>

                <div className={styles.form}>
                  <div className={styles.otpWrapper}>
                    {otpArray.map((digit, i) => (
                      <input
                        key={i}
                        id={`modal-otp-${i}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, i)}
                        onKeyDown={(e) => handleOtpKeyDown(e, i)}
                        maxLength={1}
                        className={styles.otpInput}
                        autoFocus={i === 0}
                      />
                    ))}
                  </div>

                  {otpError && (
                    <div className={styles.errorText} style={{ textAlign: "center" }}>
                      {otpError}
                    </div>
                  )}

                  <button
                    className={styles.btnPrimary}
                    onClick={handleVerifyOtp}
                    disabled={isLoading}
                  >
                    <span>{isLoading ? "Verifying..." : "Verify"}</span>
                    {!isLoading && <span className={styles.btnArrow}>→</span>}
                  </button>

                  <div className={styles.resendRow}>
                    Didn&apos;t receive the code?{" "}
                    <button
                      type="button"
                      className={styles.resendBtn}
                      onClick={handleResendOtp}
                      disabled={isLoading}
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Bottom Full-Width Frosted Pill Footer */}
        <div className={styles.bottomBar}>
          <p className={styles.bottomBarText}>
            Already have an account?{" "}
            <button type="button" className={styles.bottomBarLoginBtn} onClick={handleGoToLogin}>
              Log in <span className={styles.arrowIcon}>→</span>
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
